import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  PlusIcon,
  TrashIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { effectiveCapitalApi } from '../../helpers/effectiveCapitalApi';
import { entityLabel } from '../../helpers/effectiveCapitalFormat';
import { ConnectAccountModal } from './ConnectAccountModal';
import { LoaderSpinner } from '../LoaderSpinner';

function usePlaidScript() {
  const [loaded, setLoaded] = useState(Boolean(window.Plaid));

  useEffect(() => {
    if (window.Plaid) {
      setLoaded(true);
      return undefined;
    }
    const script = document.createElement('script');
    script.src = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
    script.onload = () => setLoaded(true);
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) document.head.removeChild(script);
    };
  }, []);

  return loaded;
}

function errorCodeLabel(code) {
  if (!code) return 'Connection error';
  if (code === 'ITEM_LOGIN_REQUIRED') return 'Login expired — re-authentication required';
  if (code === 'PRODUCT_NOT_READY') return 'Product not ready';
  if (code === 'PRODUCTS_NOT_SUPPORTED') return 'Liabilities not supported for this institution';
  return code;
}

export const EffectiveCapitalSummaryConnectAccounts = ({ onLoadingChange, viewSwitcher } = {}) => {
  const plaidLoaded = usePlaidScript();
  const [selectedEntity, setSelectedEntity] = useState('kings_loot');
  const [linkToken, setLinkToken] = useState(null);
  const [reAuthItemId, setReAuthItemId] = useState(null);
  const [accountSelectionMode, setAccountSelectionMode] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [isCreatingToken, setIsCreatingToken] = useState(false);
  const [isDetecting, setIsDetecting] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const [depositoryOnly, setDepositoryOnly] = useState(false);
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadItems = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await effectiveCapitalApi.getPlaidItemsAll();
      setItems(data);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to load connected items', { position: 'bottom-right' });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  const exchangeToken = useCallback(async (publicToken, institutionName) => {
    setIsLinking(true);
    try {
      await effectiveCapitalApi.exchangeToken({
        publicToken,
        entity: selectedEntity,
        institutionName,
      });
      toast.success('Account connected successfully', { position: 'bottom-right' });
      setLinkToken(null);
      loadItems();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to connect account', { position: 'bottom-right' });
    } finally {
      setIsLinking(false);
    }
  }, [selectedEntity, loadItems]);

  const clearReAuthError = useCallback(async (itemId) => {
    try {
      await effectiveCapitalApi.clearItemError(itemId);
      effectiveCapitalApi.syncBalances().catch(() => {});
      effectiveCapitalApi.syncLiabilities().catch(() => {});
    } catch {
      // best-effort after re-auth
    }
  }, []);

  useEffect(() => {
    if (!linkToken || !plaidLoaded || !window.Plaid) return undefined;

    const handler = window.Plaid.create({
      token: linkToken,
      onSuccess: async (publicToken, metadata) => {
        if (reAuthItemId) {
          await clearReAuthError(reAuthItemId);
          toast.success(
            accountSelectionMode
              ? `Account access updated for ${metadata?.institution?.name ?? 'account'} — syncing balances...`
              : `Re-authenticated ${metadata?.institution?.name ?? 'account'} — syncing liabilities...`,
            { position: 'bottom-right' }
          );
          setReAuthItemId(null);
          setAccountSelectionMode(false);
          setLinkToken(null);
          loadItems();
        } else {
          exchangeToken(publicToken, metadata?.institution?.name);
        }
      },
      onExit: (err) => {
        if (err) {
          toast.error(`Plaid Link exited: ${err.display_message ?? err.error_message}`, { position: 'bottom-right' });
        }
        setLinkToken(null);
        setReAuthItemId(null);
        setAccountSelectionMode(false);
      },
    });

    handler.open();
    return () => handler.destroy();
  }, [linkToken, plaidLoaded, reAuthItemId, accountSelectionMode, exchangeToken, clearReAuthError, loadItems]);

  const openLinkToken = async (mode, itemId = null, { accountSelection = false } = {}) => {
    setIsCreatingToken(true);
    setAccountSelectionMode(accountSelection);
    try {
      let data;
      if (itemId) {
        setReAuthItemId(itemId);
        data = await effectiveCapitalApi.createUpdateModeLinkToken(itemId, accountSelection);
      } else if (mode === 'depository') {
        data = await effectiveCapitalApi.createDepositoryLinkToken();
      } else {
        data = await effectiveCapitalApi.createLinkToken();
      }
      setLinkToken(data.linkToken);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to create link token', { position: 'bottom-right' });
      setReAuthItemId(null);
      setAccountSelectionMode(false);
    } finally {
      setIsCreatingToken(false);
    }
  };

  const handleDetectErrors = async () => {
    setIsDetecting(true);
    try {
      const results = await effectiveCapitalApi.detectErrors();
      await loadItems();
      const errCount = Array.isArray(results) ? results.filter((r) => r.status === 'error').length : 0;
      const refreshed = await effectiveCapitalApi.getPlaidItemsAll().catch(() => items);
      const staleCount = refreshed.filter((i) => i.stale && i.status === 'active' && !i.errorCode).length;
      const disconnectedCount = refreshed.filter((i) => i.status === 'disconnected').length;
      if (errCount > 0) {
        toast.warning(`${errCount} connection(s) need re-authentication`, { position: 'bottom-right' });
      } else if (disconnectedCount > 0) {
        toast.warning(`${disconnectedCount} disconnected connection(s) have stale data — reconnect from the list below`, { position: 'bottom-right' });
      } else if (staleCount > 0) {
        toast.warning(`${staleCount} connection(s) have not synced in >25 hours — check the list below`, { position: 'bottom-right' });
      } else {
        toast.success('All connections are healthy', { position: 'bottom-right' });
      }
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Health check failed', { position: 'bottom-right' });
    } finally {
      setIsDetecting(false);
    }
  };

  const handleRemove = async (item) => {
    if (!window.confirm(`Disconnect ${item.institutionName ?? item.institutionId}?`)) return;
    try {
      await effectiveCapitalApi.removePlaidItem(item.itemId);
      toast.success('Account disconnected', { position: 'bottom-right' });
      loadItems();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to remove item', { position: 'bottom-right' });
    }
  };

  const staleItems = useMemo(
    () => items.filter((i) => i.stale && i.status === 'active' && !i.errorCode),
    [items]
  );
  const erroredItems = useMemo(
    () => items.filter((i) => i.status === 'error' || i.errorCode),
    [items]
  );
  const disconnectedItems = useMemo(
    () => items.filter((i) => i.status === 'disconnected'),
    [items]
  );
  const activeItems = useMemo(
    () => items.filter((i) => i.status === 'active' && !i.errorCode && !i.stale),
    [items]
  );

  const canReAuth = (item) =>
    !item.errorCode ||
    item.errorCode === 'ITEM_LOGIN_REQUIRED' ||
    item.errorCode === 'UNKNOWN' ||
    item.status === 'disconnected' ||
    item.stale;

  const errorCount = items.filter((item) => item.status !== 'active' || item.errorCode).length;
  const AttentionItem = ({ item, variant, actionLabel }) => {
    const showReAuth = variant !== 'error' || canReAuth(item);
    return (
      <div className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <span className={`mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
            variant === 'disconnected' ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-600'
          }`}>
            <ExclamationTriangleIcon className="h-3.5 w-3.5" />
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-900">{item.institutionName ?? 'Unknown Institution'}</span>
              <span className="text-xs text-gray-500">{entityLabel(item.entity)}</span>
            </div>
            <p className="mt-0.5 text-xs text-gray-500">
              {variant === 'stale' && (
                <>
                  Last account sync: {item.latestAccountSyncedAt ? new Date(item.latestAccountSyncedAt).toLocaleString() : 'never'}.
                  {' '}Plaid reports no error — may need re-authentication.
                </>
              )}
              {variant === 'error' && errorCodeLabel(item.errorCode)}
              {variant === 'disconnected' && (
                <>
                  Disconnected — accounts are using stale balances from{' '}
                  {item.lastSyncedAt ? new Date(item.lastSyncedAt).toLocaleDateString() : 'unknown date'}.
                  {' '}Reconnect to resume daily sync.
                </>
              )}
            </p>
            {variant === 'error' && item.errorMessage && item.errorCode !== 'ITEM_LOGIN_REQUIRED' && (
              <p className="mt-1 line-clamp-2 text-xs text-gray-500">{item.errorMessage}</p>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {showReAuth && (
            <button
              type="button"
              onClick={() => openLinkToken('update', item.itemId)}
              disabled={isCreatingToken || !plaidLoaded}
              className="flex h-8 items-center gap-1.5 rounded-md bg-gray-900 px-3 text-[13px] font-medium text-white hover:bg-gray-800 disabled:opacity-50"
            >
              <ArrowPathIcon className="h-4 w-4" />
              {actionLabel}
            </button>
          )}
          <button
            type="button"
            onClick={() => handleRemove(item)}
            className="flex h-8 items-center gap-1.5 rounded-md px-2 text-[13px] font-medium text-gray-500 hover:bg-gray-100 hover:text-red-600"
          >
            <TrashIcon className="h-4 w-4" />
            Remove
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-3 py-3 border-b border-gray-200 mb-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          {viewSwitcher || <h2 className="text-sm font-bold uppercase tracking-widest">Connect Accounts</h2>}
          <p className="mt-1 text-sm text-gray-500">Link bank accounts, credit cards, and lines of credit via Plaid</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleDetectErrors}
            disabled={isDetecting}
            className="flex h-8 items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 text-[13px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-4 w-4 ${isDetecting ? 'animate-spin' : ''}`} />
            Check Connection Health
          </button>
          <button
            type="button"
            onClick={() => setConnectOpen(true)}
            disabled={!plaidLoaded || isCreatingToken || isLinking}
            className="flex h-8 items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 text-[13px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <PlusIcon className="h-4 w-4" />
            {isCreatingToken && !reAuthItemId ? 'Preparing Plaid Link...' : isLinking ? 'Connecting...' : 'Connect Account'}
          </button>
        </div>
      </div>

      <div>
        {(staleItems.length + disconnectedItems.length + erroredItems.length) > 0 && (
          <div className="mb-6 bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <h2 className="text-sm font-semibold text-gray-900">Needs Attention</h2>
              <div className="flex flex-wrap justify-end gap-2">
                {staleItems.length > 0 && (
                  <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-200">
                    {staleItems.length} sync overdue
                  </span>
                )}
                {erroredItems.length > 0 && (
                  <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-200">
                    {erroredItems.length} need attention
                  </span>
                )}
                {disconnectedItems.length > 0 && (
                  <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-200">
                    {disconnectedItems.length} disconnected
                  </span>
                )}
              </div>
            </div>
            <div className="divide-y divide-gray-200">
              {disconnectedItems.map((item) => (
                <AttentionItem key={item.id} item={item} variant="disconnected" actionLabel="Reconnect" />
              ))}
              {erroredItems.map((item) => (
                <AttentionItem key={item.id} item={item} variant="error" actionLabel="Re-authenticate" />
              ))}
              {staleItems.map((item) => (
                <AttentionItem key={item.id} item={item} variant="stale" actionLabel="Re-authenticate" />
              ))}
            </div>
          </div>
        )}

        {activeItems.length > 0 && (
          <div className="mb-6 flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
            <InformationCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
            <div>
              <span className="font-semibold">To get min payment, next due date, last payment, and APR data:</span>{' '}
              Remove each institution below and re-connect it. New connections include Liabilities consent.
              After reconnecting, run <span className="font-semibold">Sync Liabilities</span> from Cashflow Export.
            </div>
          </div>
        )}

        <div className="mb-6 bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <h2 className="text-sm font-semibold text-gray-900">Active Connections</h2>
            <span className="text-xs text-gray-500">{activeItems.length} connection{activeItems.length === 1 ? '' : 's'}</span>
          </div>
          {isLoading ? (
            <div className="flex h-32 items-center justify-center text-sm text-gray-500">
              <LoaderSpinner height="80" width="80" colorSpinner="black" border="4" />
            </div>
          ) : activeItems.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <BuildingOffice2Icon className="mx-auto mb-2 h-6 w-6 text-gray-400" />
              <p className="text-sm text-gray-500">No active connections</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    <th className="px-4 py-2.5">Institution</th>
                    <th className="px-4 py-2.5">Entity</th>
                    <th className="px-4 py-2.5">Status</th>
                    <th className="px-4 py-2.5 text-right">Last Synced</th>
                    <th className="px-4 py-2.5 text-right">Last Reconnected</th>
                    <th className="px-4 py-2.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {activeItems.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5 font-medium text-gray-900">{item.institutionName ?? item.itemId ?? 'Unknown'}</td>
                      <td className="px-4 py-2.5 text-gray-700">{entityLabel(item.entity)}</td>
                      <td className="px-4 py-2.5">
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200">
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                          Active
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right text-gray-500 tabular-nums">
                        {(item.latestAccountSyncedAt ?? item.lastSyncedAt)
                          ? new Date(item.latestAccountSyncedAt ?? item.lastSyncedAt).toLocaleString()
                          : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-right text-xs text-gray-400 tabular-nums">
                        {item.lastReconnectedAt ? new Date(item.lastReconnectedAt).toLocaleString() : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => openLinkToken('update', item.itemId)}
                            disabled={isCreatingToken || !plaidLoaded}
                            className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50"
                            title="Reconnect — re-open Plaid Link to refresh consent and pull extended transaction history"
                          >
                            <ArrowPathIcon className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => openLinkToken('update', item.itemId, { accountSelection: true })}
                            disabled={isCreatingToken || !plaidLoaded}
                            className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50"
                            title="Add Accounts — grant access to accounts opened after this connection was created"
                          >
                            <PlusIcon className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemove(item)}
                            className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-red-600"
                            title="Remove"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="border-t border-gray-200 px-4 py-2.5 text-xs text-gray-500">
            Actions: <span className="text-gray-700">Reconnect</span> re-opens Plaid to refresh consent and pull extended transaction history · <span className="text-gray-700">Add Accounts</span> opens the account picker for newly opened accounts · <span className="text-gray-700">Remove</span> disconnects the institution.
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">All Institutions</h2>
              <p className="text-xs text-gray-500">Every Plaid item on file, including errored and disconnected ones</p>
            </div>
            <span className="text-xs text-gray-500">
              {items.length} institutions{errorCount > 0 ? ` · ${errorCount} error${errorCount === 1 ? '' : 's'}` : ''}
            </span>
          </div>
          {isLoading ? null : items.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <BuildingOffice2Icon className="mx-auto mb-2 h-6 w-6 text-gray-400" />
              <p className="text-sm text-gray-500">No accounts connected yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    <th className="px-4 py-2.5">Institution</th>
                    <th className="px-4 py-2.5">Entity</th>
                    <th className="px-4 py-2.5">Status</th>
                    <th className="px-4 py-2.5 text-right">Last Synced</th>
                    <th className="px-4 py-2.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {items.map((item) => {
                    const isHealthy = item.status === 'active' && !item.errorCode;
                    return (
                      <tr key={item.id} className={`hover:bg-gray-50 ${!isHealthy ? 'bg-red-50/30' : ''}`}>
                        <td className="px-4 py-2.5 font-medium text-gray-900">{item.institutionName ?? item.institutionId ?? 'Unknown'}</td>
                        <td className="px-4 py-2.5 text-gray-700">{entityLabel(item.entity)}</td>
                        <td className="px-4 py-2.5">
                          <span className={`inline-flex items-center gap-1 text-xs font-medium ${isHealthy ? 'text-emerald-700' : 'text-red-600'}`}>
                            {isHealthy ? <CheckCircleIcon className="h-3.5 w-3.5" /> : <XCircleIcon className="h-3.5 w-3.5" />}
                            {item.errorCode ?? item.status}
                            {item.stale && item.status === 'active' && !item.errorCode ? ' (stale)' : ''}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right text-gray-500 tabular-nums">
                          {(item.latestAccountSyncedAt ?? item.lastSyncedAt)
                            ? new Date(item.latestAccountSyncedAt ?? item.lastSyncedAt).toLocaleString()
                            : '—'}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <button
                            type="button"
                            onClick={() => handleRemove(item)}
                            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-gray-500 hover:bg-gray-100 hover:text-red-600"
                          >
                            <TrashIcon className="h-3.5 w-3.5" />
                            Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <ConnectAccountModal
        open={connectOpen}
        onClose={() => setConnectOpen(false)}
        entity={selectedEntity}
        onEntityChange={setSelectedEntity}
        depositoryOnly={depositoryOnly}
        onDepositoryChange={setDepositoryOnly}
        onContinue={() => {
          setConnectOpen(false);
          openLinkToken(depositoryOnly ? 'depository' : 'standard');
        }}
        disabled={!plaidLoaded || isCreatingToken || isLinking}
      />
    </div>
  );
};
